import { isQuestionSemanticallyBoundToFact } from '../../../lib/audit/semanticFactBinding';
/**
 * Truth-question resolution (2026-08-05, upgraded 2026-10-21).
 *
 * Supports the full ordinary-user audit-resolution journey:
 *  - Value corrections written to canonical SED paths (`saveSEDAmendment`)
 *  - Operator attestations (`confirmed`), explicit labelled feasibility
 *    assumptions (`feasibility-assumption`), and direct statement/fact wording
 *    corrections (`correct-claim`) persisted to site audit overrides and/or
 *    immutable report revisions (`onApplyReportCorrection`)
 *  - Always-visible source editing, evidence document/file attachment,
 *    explicit assumption labelling, and rationale recording.
 */
import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { useStore } from '../../../store';
import { factIdToSedPath, factIdToUnit } from '../../../lib/grounding/buildCanonicalFactStore';
import { acceptedUnits, isQuantityError, parseUserQuantity } from '../../../lib/units/userQuantity';
import {
  ATTESTATION_CONFIRMED,
  ATTESTATION_RETRACT,
} from '../../../lib/audit/qaSeverityPolicy';
import {
  buildTruthAttestation,
  classifyTruthQuestion,
  computeAuditOverridePayloadSignature,
  computeAuditOverridesFingerprint,
  computeAuditOverridesVersion,
  computeSedAmendmentPayloadSignature,
  generateAuditOperationId,
  retainedUnknownByCheckId,
  stripHtmlComments,
} from '../../../lib/audit/truthAttestations';
import type { SiteAuditOverride } from '../../../types/site';
import type { TruthQuestion } from '../../../lib/audit/qaSeverityPolicy';
import type { SEDAmendment, AmendmentSource } from '../../../types/sedAmendments';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';

export const ATTESTATION_ASSUMPTION = 'feasibility-assumption';
export const ATTESTATION_CORRECT_CLAIM = 'correct-claim';
export const ATTESTATION_UNKNOWN = 'retain-unknown';

export function formatCandidateDisplayLabel(candidate: { value: string; label: string }): string {
  const val = (candidate.value ?? '').trim();
  const lbl = (candidate.label ?? '').trim();
  if (
    !val
    || val === ATTESTATION_CONFIRMED
    || val === ATTESTATION_RETRACT
    || val === ATTESTATION_ASSUMPTION
    || val === ATTESTATION_CORRECT_CLAIM
    || val === ATTESTATION_UNKNOWN
  ) {
    return lbl;
  }
  if (lbl.includes(val)) return lbl;
  return `${lbl}: ${val}`;
}

export interface TruthResolutionApplyCorrectionItem {
  questionId: string;
  detector: string;
  originalClaim: string;
  replacementText: string;
  evidenceCitation: string;
  assumptionLabel?: string;
  rationale: string;
  sourceType?: string;
  evidenceDocumentId?: string;
  evidenceAttachment?: string;
}

export interface TruthResolutionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  questions: readonly TruthQuestion[];
  sed: Pick<SiteEvidenceDocument, 'id' | 'version'> | null | undefined;
  /** Site the report belongs to — attestations persist as site audit overrides. */
  siteId?: string;
  /** Report type, so an attestation does not leak across report types. */
  reportTypeId?: string;
  /** Optional site audit overrides (defaults to reading the site from store). */
  auditOverrides?: readonly SiteAuditOverride[];
  /** Site documents available to attach as supporting evidence. */
  siteDocuments?: ReadonlyArray<{ id: string; name?: string; fileName?: string; siteId?: string; category?: string }>;
  /** Optional callback to apply statement/fact corrections directly to a new immutable report revision. */
  reportRevisionId?: string;
  onApplyReportCorrection?: (
    items: TruthResolutionApplyCorrectionItem[],
    options?: {
      operationId?: string;
      expectedRevisionId?: string;
      expectedAuditVersion?: number;
      expectedAuditFingerprint?: string;
      expectedSedVersion?: number;
    },
  ) => Promise<{ applied: boolean; conflict?: boolean; code?: string; error?: string; reason?: string }>;
  /** Optional callback to refresh the host's site/SED/override baseline after a concurrent save conflict. */
  onRefreshBaseline?: () => Promise<{
    sed?: Pick<SiteEvidenceDocument, 'id' | 'version'> | null;
    auditOverrides?: readonly SiteAuditOverride[];
    auditOverridesVersion?: number;
  } | void> | void;
  /** Invoked after at least one answer persisted, so the host can offer a rerun. */
  onResolved?: (resolvedCount: number) => void;
  /**
   * Open the rerun flow. A "No — regenerate this report" answer settles
   * nothing to SAVE, but its remedy is the rerun — without this the dialog
   * told the user to rerun while offering no way to do it (William,
   * 2026-08-25: "i cant save if i select no").
   */
  onRerun?: () => void;
  /** Navigate to the Audit tab's Report-quality section for this report. */
  onOpenQuality?: () => void;
}

/** How the operator knows — mirrors the Variable Browser's source picker. */
const SOURCE_OPTIONS: ReadonlyArray<{ value: AmendmentSource['type']; label: string }> = [
  { value: 'expert-judgment', label: 'Expert judgment' },
  { value: 'document', label: 'A document on file' },
  { value: 'site-visit', label: 'Site visit' },
  { value: 'external-link', label: 'External source' },
];

const ASSUMPTION_PRESETS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'None (verified fact)' },
  { value: 'Feasibility-stage planning assumption', label: 'Feasibility-stage planning assumption' },
  { value: 'Indicative screening estimate', label: 'Indicative screening estimate' },
  { value: 'Water-use screening / sensitivity estimate', label: 'Water-use screening / sensitivity estimate' },
  { value: 'Unknown / unverified at screening stage (retain absent)', label: 'Unknown / unverified at screening stage (retain absent)' },
  { value: 'Pending utility interconnection study', label: 'Pending utility interconnection study' },
  { value: 'Pending site / geotechnical investigation', label: 'Pending site / geotechnical investigation' },
  { value: 'Preliminary engineering assumption', label: 'Preliminary engineering assumption' },
];

const FREE_TEXT = '__free__';

interface Answer {
  choice: string;
  freeText: string;
  sourceType: AmendmentSource['type'];
  sourceCitation: string;
  assumptionLabel: string;
  evidenceDocumentId: string;
  evidenceNote: string;
  attachedFileName: string;
  replacementText: string;
  /** Why the operator stands behind the statement, assumption, or correction. */
  rationale: string;
}

const ENGINE_FIGURE_QUESTION = /computed by the project-finance engine/i;

export function resolveWritablePath(question: TruthQuestion): string | undefined {
  if (!question.factId) return undefined;
  if (!isQuestionSemanticallyBoundToFact(question)) return undefined;
  return factIdToSedPath(question.factId);
}

/**
 * Retaining unknown is only valid when canonical evidence for the governed fact
 * is absent/unverified or when the question is contextual/unbound. A bound
 * value question that has a canonical evidence value (or reconciles conflicting
 * numbers in the report) is a supported numeric contradiction and cannot be
 * bypassed by retaining unknown.
 */
export function canRetainUnknown(question: TruthQuestion): boolean {
  if (question.kind === 'value' && resolveWritablePath(question) !== undefined) {
    if (question.detector === 'internalNumberFindings') return false;
    if (question.candidates.length >= 2) return false;
    const hasCanonicalCandidate = question.candidates.some((c) =>
      /^canonical evidence value\b|\(sed\)/i.test(c.label.trim()),
    );
    if (hasCanonicalCandidate) return false;
  }
  return true;
}

export const TruthResolutionDialog: React.FC<TruthResolutionDialogProps> = ({
  open,
  onOpenChange,
  onRerun,
  onOpenQuality,
  questions,
  sed,
  siteId,
  reportTypeId,
  auditOverrides,
  siteDocuments,
  reportRevisionId,
  onApplyReportCorrection,
  onRefreshBaseline,
  onResolved,
}) => {
  const saveSEDAmendment = useStore(s => s.saveSEDAmendment);
  const recordAuditOverride = useStore(s => s.recordAuditOverride);
  const rawStoreDocuments = useStore(s => (s as { siteRag?: { documents?: Array<{ id: string; name: string; siteId?: string; category?: string }> } }).siteRag?.documents);
  const storeSite = useStore(s => (
    siteId
      ? (s as { siteRag?: { sites?: Array<{ id: string; documents?: Array<{ id: string; name: string; siteId?: string; category?: string }>; auditOverrides?: SiteAuditOverride[]; auditOverridesVersion?: number; sedId?: string; sedVersion?: number; sedStoragePath?: string }> } })
          .siteRag?.sites?.find(site => site.id === siteId)
      : undefined
  ));
  const rawStoreReports = useStore(
    s => (s as { siteRag?: { reports?: Array<{ siteId?: string; documents?: Array<{ id: string; name: string; siteId?: string; category?: string }> }> } }).siteRag?.reports,
  );
  const availableDocuments = useMemo(() => {
    const seen = new Set<string>();
    const docs: Array<{ id: string; name: string; siteId?: string; category?: string }> = [];
    const pushDoc = (
      d:
        | { id?: string; name?: string; fileName?: string; siteId?: string; category?: string }
        | undefined
        | null,
    ) => {
      if (!d || typeof d.id !== 'string' || !d.id || seen.has(d.id)) return;
      if (siteId && d.siteId && d.siteId !== siteId) return;
      const resolvedName =
        typeof d.name === 'string' && d.name.trim()
          ? d.name
          : typeof d.fileName === 'string' && d.fileName.trim()
            ? d.fileName
            : d.id;
      seen.add(d.id);
      docs.push({
        id: d.id,
        name: resolvedName,
        ...(d.siteId ? { siteId: d.siteId } : {}),
        ...(d.category ? { category: d.category } : {}),
      });
    };
    for (const d of siteDocuments ?? []) pushDoc(d);
    for (const d of storeSite?.documents ?? []) pushDoc(d);
    if (siteId && Array.isArray(rawStoreReports)) {
      for (const r of rawStoreReports) {
        if (r.siteId && r.siteId !== siteId) continue;
        for (const d of r.documents ?? []) pushDoc(d);
      }
    }
    if (docs.length === 0 && !siteDocuments && Array.isArray(rawStoreDocuments)) {
      for (const d of rawStoreDocuments) {
        if (siteId && d.siteId && d.siteId !== siteId) continue;
        pushDoc(d);
      }
    }
    return docs;
  }, [siteDocuments, storeSite?.documents, rawStoreReports, rawStoreDocuments, siteId]);
  const storeSiteOverrides = storeSite?.auditOverrides;
  const [refreshedOverrides, setRefreshedOverrides] = useState<readonly SiteAuditOverride[] | undefined>(undefined);
  const [refreshedAuditVersion, setRefreshedAuditVersion] = useState<number | undefined>(undefined);
  const [refreshedSed, setRefreshedSed] = useState<Pick<SiteEvidenceDocument, 'id' | 'version'> | null | undefined>(undefined);
  const [conflictNotice, setConflictNotice] = useState<string | null>(null);
  const [, setDraftRevision] = useState<number>(1);
  const operationSlotsRef = useRef<
    Map<
      string,
      {
        operationId: string;
        payloadSignature: string;
        expectedRevisionId?: string;
        expectedAuditVersion?: number;
        expectedAuditFingerprint?: string;
        expectedSedVersion?: number;
      }
    >
  >(new Map());

  const resolveQuestionCommandEnvelope = (
    slotKey: string,
    prefix: string,
    payloadSignature: string,
    baseline: {
      expectedRevisionId?: string;
      expectedAuditVersion?: number;
      expectedAuditFingerprint?: string;
      expectedSedVersion?: number;
    },
  ) => {
    const existing = operationSlotsRef.current.get(slotKey);
    if (existing && existing.payloadSignature === payloadSignature) {
      return existing;
    }
    const envelope = {
      operationId: generateAuditOperationId(prefix),
      payloadSignature,
      ...baseline,
    };
    operationSlotsRef.current.set(slotKey, envelope);
    return envelope;
  };

  const hasLoadedAuditBaseline =
    refreshedAuditVersion !== undefined ||
    refreshedOverrides !== undefined ||
    auditOverrides !== undefined ||
    storeSite !== undefined;
  const effectiveOverrides = refreshedOverrides ?? auditOverrides ?? storeSiteOverrides;
  const baselineAuditVersion = useMemo(
    () =>
      hasLoadedAuditBaseline
        ? refreshedAuditVersion ??
          computeAuditOverridesVersion({
            auditOverrides: effectiveOverrides,
            auditOverridesVersion: storeSite?.auditOverridesVersion,
          })
        : undefined,
    [hasLoadedAuditBaseline, refreshedAuditVersion, effectiveOverrides, storeSite?.auditOverridesVersion],
  );
  const baselineAuditFingerprint = useMemo(
    () =>
      hasLoadedAuditBaseline
        ? computeAuditOverridesFingerprint(effectiveOverrides)
        : undefined,
    [hasLoadedAuditBaseline, effectiveOverrides],
  );

  const retainedUnknownMap = useMemo(
    () => retainedUnknownByCheckId(effectiveOverrides, reportTypeId),
    [effectiveOverrides, reportTypeId],
  );

  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [isSaving, setIsSaving] = useState(false);

  const getAnswer = (questionId: string): Answer | undefined => {
    if (answers[questionId]) return answers[questionId];
    const persisted = retainedUnknownMap.get(questionId);
    if (!persisted) return undefined;
    return {
      choice: ATTESTATION_UNKNOWN,
      freeText: '',
      sourceType: (persisted.sourceType as AmendmentSource['type']) ?? 'expert-judgment',
      sourceCitation: persisted.sourceCitation ?? '',
      assumptionLabel:
        persisted.assumptionLabel ?? 'Unknown / unverified at screening stage (retain absent)',
      evidenceDocumentId: persisted.evidenceDocumentId ?? '',
      evidenceNote: persisted.evidenceAttachment ?? '',
      attachedFileName: '',
      replacementText: '',
      rationale: persisted.rationale,
    };
  };

  const normalizedQuestions = useMemo<TruthQuestion[]>(
    () => questions.map((q) => {
      const cleanedClaim = stripHtmlComments(q.reportClaim) || q.reportClaim;
      return cleanedClaim !== q.reportClaim ? { ...q, reportClaim: cleanedClaim } : q;
    }),
    [questions],
  );

  const isContextualUnboundQuestion = (q: TruthQuestion): boolean =>
    q.kind === 'value' && resolveWritablePath(q) === undefined;

  const answerable = useMemo(
    () => normalizedQuestions.filter(q => (
      q.kind === 'attestation'
        ? Boolean(siteId)
        : resolveWritablePath(q) !== undefined || (q.kind === 'value' && Boolean(siteId))
    )),
    [normalizedQuestions, siteId],
  );

  const advisoryOnly = useMemo(
    () => normalizedQuestions.filter(
      q => q.kind !== 'attestation' && resolveWritablePath(q) === undefined,
    ).length,
    [normalizedQuestions],
  );
  const unanswerable = normalizedQuestions.length - answerable.length;
  const remainderReason = advisoryOnly === unanswerable
    ? 'not tied to a stored evidence field'
    : advisoryOnly === 0
      ? 'not attached to a site record that can hold an attestation'
      : 'neither tied to a stored evidence field nor attached to a site record';

  const update = (id: string, patch: Partial<Answer>) => {
    setDraftRevision(r => r + 1);
    setAnswers(prev => {
      const existing: Answer = prev[id] ?? getAnswer(id) ?? {
        choice: '',
        freeText: '',
        sourceType: 'expert-judgment',
        sourceCitation: '',
        assumptionLabel: '',
        evidenceDocumentId: '',
        evidenceNote: '',
        attachedFileName: '',
        replacementText: '',
        rationale: '',
      };
      return { ...prev, [id]: { ...existing, ...patch } };
    });
  };

  const handleRefreshConflict = async (): Promise<void> => {
    try {
      const custom = await onRefreshBaseline?.();
      let authoritativeSite: {
        id: string;
        auditOverrides?: readonly SiteAuditOverride[];
        auditOverridesVersion?: number;
        sedId?: string;
        sedVersion?: number;
        sedStoragePath?: string;
      } | null | undefined;
      let authoritativeSed: Pick<SiteEvidenceDocument, 'id' | 'version'> | null | undefined;

      if (custom && typeof custom === 'object') {
        if (custom.auditOverrides !== undefined || custom.auditOverridesVersion !== undefined) {
          authoritativeSite = {
            id: siteId ?? 'site',
            auditOverrides: custom.auditOverrides,
            auditOverridesVersion: custom.auditOverridesVersion,
          };
        }
        if (custom.sed !== undefined) {
          authoritativeSed = custom.sed;
        }
      }

      if (siteId && (!authoritativeSite || (Boolean(sed?.id || storeSite?.sedId) && !authoritativeSed))) {
        const refreshFn = ((typeof useStore.getState === "function" ? useStore.getState() : {}) as {
          refreshSiteBaseline?: (id: string) => Promise<{
            site?: {
              id: string;
              auditOverrides?: SiteAuditOverride[];
              auditOverridesVersion?: number;
              sedId?: string;
              sedVersion?: number;
              sedStoragePath?: string;
            } | null;
            sed?: Pick<SiteEvidenceDocument, 'id' | 'version'> | null;
          }>;
        }).refreshSiteBaseline;
        if (typeof refreshFn !== 'function') {
          throw new Error('Authoritative baseline refresh is unavailable.');
        }
        const refreshed = await refreshFn(siteId);
        if (!refreshed?.site) {
          throw new Error('Authoritative site state could not be loaded.');
        }
        if (!authoritativeSite) authoritativeSite = refreshed.site;
        if (!authoritativeSed && refreshed.sed) {
          authoritativeSed = { id: refreshed.sed.id, version: refreshed.sed.version };
        }
        if ((refreshed.site.sedId || sed?.id) && !authoritativeSed) {
          throw new Error('Authoritative SED state could not be loaded.');
        }
      }

      if (!authoritativeSite && !authoritativeSed) {
        throw new Error('Authoritative baseline could not be loaded.');
      }

      if (authoritativeSite) {
        setRefreshedOverrides(authoritativeSite.auditOverrides ?? []);
        setRefreshedAuditVersion(
          computeAuditOverridesVersion({
            auditOverrides: authoritativeSite.auditOverrides,
            auditOverridesVersion: authoritativeSite.auditOverridesVersion,
          }),
        );
      }
      if (authoritativeSed) {
        setRefreshedSed(authoritativeSed);
      }
      operationSlotsRef.current.clear();
      setDraftRevision((r) => r + 1);
      setConflictNotice(null);
      toast.success('Latest site evidence and audit state loaded. Review your draft and click Save to retry.');
    } catch (e) {
      toast.error(`Could not refresh baseline: ${(e as Error)?.message ?? 'unknown error'}`);
    }
  };

  const unitOf = (question: TruthQuestion): string | undefined =>
    question.factId ? factIdToUnit(question.factId) : undefined;

  const settledValue = (
    question: TruthQuestion,
  ): { value: number | string } | { error: string } | undefined => {
    const answer = answers[question.id];
    if (
      !answer?.choice
      || answer.choice === ATTESTATION_ASSUMPTION
      || answer.choice === ATTESTATION_CORRECT_CLAIM
      || answer.choice === ATTESTATION_UNKNOWN
      || answer.choice === ATTESTATION_CONFIRMED
    ) {
      return undefined;
    }
    const raw = answer.choice === FREE_TEXT ? answer.freeText.trim() : answer.choice;
    if (!raw) return undefined;

    const unit = unitOf(question);
    if (!unit) return { value: raw };

    const parsed = parseUserQuantity(raw, unit);
    if (isQuantityError(parsed)) return { error: parsed.error };
    return { value: parsed.value };
  };

  const echoFor = (question: TruthQuestion): string | null => {
    const answer = answers[question.id];
    if (answer?.choice !== FREE_TEXT || !answer.freeText.trim()) return null;
    const unit = unitOf(question);
    if (!unit) return null;
    const parsed = parseUserQuantity(answer.freeText, unit);
    if (isQuantityError(parsed)) return parsed.error;
    if (parsed.converted) return `= ${Number(parsed.value.toPrecision(6))} ${unit} (from ${parsed.inputUnit})`;
    if (parsed.assumedCanonical) return `Interpreted as ${unit}.`;
    return null;
  };

  const retractRemedy = (question: TruthQuestion): string =>
    ENGINE_FIGURE_QUESTION.test(question.question)
      ? 'Correct the financial assumptions, then re-stamp this report.'
      : 'Rerun the report to regenerate this section.';

  const MIN_RATIONALE = 12;
  const isOverrideChoice = (choice: string | undefined): boolean =>
    choice === ATTESTATION_CONFIRMED
    || choice === ATTESTATION_UNKNOWN
    || choice === ATTESTATION_ASSUMPTION
    || choice === ATTESTATION_CORRECT_CLAIM;

  const isRetainedUnknownReady = (q: TruthQuestion): boolean => {
    if (!canRetainUnknown(q)) return false;
    const a = getAnswer(q.id);
    if (a?.choice !== ATTESTATION_UNKNOWN) return false;
    return Boolean(siteId) && a.rationale.trim().length >= MIN_RATIONALE;
  };

  const isSettled = (q: TruthQuestion): boolean => {
    const a = getAnswer(q.id);
    if (!a?.choice || a.choice === ATTESTATION_UNKNOWN) return false;
    if (a.choice === ATTESTATION_ASSUMPTION) {
      return Boolean(siteId)
        && a.rationale.trim().length >= MIN_RATIONALE
        && a.assumptionLabel.trim().length > 0;
    }
    if (a.choice === ATTESTATION_CORRECT_CLAIM) {
      return Boolean(siteId)
        && a.rationale.trim().length >= MIN_RATIONALE
        && a.replacementText.trim().length > 0;
    }
    if (q.kind === 'attestation' || isContextualUnboundQuestion(q)) {
      return a.choice === ATTESTATION_CONFIRMED && a.rationale.trim().length >= MIN_RATIONALE;
    }
    const r = settledValue(q);
    return r !== undefined && !('error' in r);
  };
  const settledCount = answerable.filter(isSettled).length;
  const retainedUnknownCount = answerable.filter(isRetainedUnknownReady).length;
  const saveableCount = settledCount + retainedUnknownCount;
  const retractCount = answerable.filter(
    (q) => q.kind === 'attestation' && getAnswer(q.id)?.choice === ATTESTATION_RETRACT,
  ).length;

  const handleSubmit = async (
    { announceResolved = true }: { announceResolved?: boolean } = {},
  ): Promise<boolean> => {
    setIsSaving(true);
    setConflictNotice(null);
    let effectiveSed: Pick<SiteEvidenceDocument, 'id' | 'version'> | null | undefined = refreshedSed ?? sed;
    if (!effectiveSed?.id && siteId) {
      try {
        const { useStore } = await import('../../../store');
        const site = useStore.getState().siteRag.sites.find((s) => s.id === siteId);
        if (site?.sedId) {
          const { loadSedById } = await import('../../../services/sedLoader');
          effectiveSed = await loadSedById(site.sedId, site.sedStoragePath, site.sedVersion);
        }
      } catch (e) {
        console.warn('[truth-resolution] last-chance SED load failed:', (e as Error)?.message);
      }
    }
    const { auth } = await import('../../../firebase');
    const user = auth.currentUser;
    if (!user) {
      setIsSaving(false);
      toast.error('Sign in again to record a correction.');
      return false;
    }

    let saved = 0;
    let resolvedSaved = 0;
    let pendingReviewSaved = 0;
    let unknownSaved = 0;
    const failures: string[] = [];
    const reportCorrectionsToApply: TruthResolutionApplyCorrectionItem[] = [];
    const pendingCorrectionFinalizations: Array<{
      question: TruthQuestion;
      finalDraftAttestation: ReturnType<typeof buildTruthAttestation>;
    }> = [];
    let runningAuditVersion: number | undefined = baselineAuditVersion;
    let runningAuditFingerprint: string | undefined = baselineAuditFingerprint;
    let runningSedVersion = effectiveSed?.version;

    for (const question of answerable) {
      const a = getAnswer(question.id);
      const settledQuestion = isSettled(question);
      const unknownQuestion = isRetainedUnknownReady(question);
      if ((!settledQuestion && !unknownQuestion) || !a) continue;

      if (question.kind === 'attestation' || isOverrideChoice(a.choice)) {
        if (!siteId) continue;
        const hasExtraMetadata =
          a.choice !== ATTESTATION_CONFIRMED
          || Boolean(a.assumptionLabel.trim())
          || Boolean(a.sourceCitation.trim())
          || Boolean(a.evidenceDocumentId.trim())
          || Boolean(a.evidenceNote.trim())
          || Boolean(a.attachedFileName.trim())
          || Boolean(a.replacementText.trim());
        const evidenceAttachment = [a.attachedFileName.trim(), a.evidenceNote.trim()]
          .filter(Boolean)
          .join(' — ') || undefined;
        const requiresReportCorrectionApply =
          (a.choice === ATTESTATION_CORRECT_CLAIM && a.replacementText.trim().length > 0)
          || (Boolean(onApplyReportCorrection) && a.choice === ATTESTATION_ASSUMPTION && a.replacementText.trim().length > 0);
        const finalResolutionKind =
          a.choice === ATTESTATION_UNKNOWN
            ? 'retained-unknown'
            : a.choice === ATTESTATION_ASSUMPTION
              ? 'feasibility-assumption'
              : a.choice === ATTESTATION_CORRECT_CLAIM
                ? 'corrected'
                : 'attested';
        const initialResolutionKind = requiresReportCorrectionApply
          ? a.choice === ATTESTATION_ASSUMPTION
            ? 'feasibility-assumption-pending'
            : 'pending-correction'
          : finalResolutionKind;
        const draftAttestation = buildTruthAttestation({
          detector: question.detector,
          message: question.reportClaim,
          checkId: question.id,
          rationale: a.rationale.trim(),
          ...(initialResolutionKind !== 'attested' ? { resolution: initialResolutionKind } : {}),
          ...(a.assumptionLabel.trim() ? { assumptionLabel: a.assumptionLabel.trim() } : {}),
          ...(hasExtraMetadata ? { sourceType: a.sourceType } : {}),
          ...(a.sourceCitation.trim() ? { sourceCitation: a.sourceCitation.trim() } : {}),
          ...(a.evidenceDocumentId.trim() ? { evidenceDocumentId: a.evidenceDocumentId.trim() } : {}),
          ...(evidenceAttachment ? { evidenceAttachment } : {}),
          ...(a.choice === ATTESTATION_CORRECT_CLAIM && a.replacementText.trim()
            ? { replacementText: a.replacementText.trim() }
            : {}),
          ...(question.reportClaim ? { priorEvidence: question.reportClaim } : {}),
          ...(reportTypeId ? { reportTypeId } : {}),
        });
        const overridePayloadSig = computeAuditOverridePayloadSignature(draftAttestation);
        const existingOverrideSlot = operationSlotsRef.current.get(`override:${siteId}:${question.id}`);
        if (
          (!existingOverrideSlot || existingOverrideSlot.payloadSignature !== overridePayloadSig) &&
          runningAuditVersion === undefined &&
          !runningAuditFingerprint
        ) {
          failures.push(
            `Could not save attestation for ${question.id}: Authoritative site audit baseline is not loaded; click Refresh latest before saving.`,
          );
          continue;
        }
        const overrideEnvelope = resolveQuestionCommandEnvelope(
          `override:${siteId}:${question.id}`,
          `truth-override:${siteId}:${question.id}`,
          overridePayloadSig,
          {
            expectedAuditVersion: runningAuditVersion,
            expectedAuditFingerprint: runningAuditFingerprint,
          },
        );
        try {
          const savedOverride = await recordAuditOverride(
            siteId,
            {
              ...draftAttestation,
              operationId: overrideEnvelope.operationId,
            },
            {
              expectedAuditVersion: overrideEnvelope.expectedAuditVersion,
              expectedAuditFingerprint: overrideEnvelope.expectedAuditFingerprint,
              operationId: overrideEnvelope.operationId,
            },
          );
          const receiptMeta = savedOverride as
            | (SiteAuditOverride & { _auditVersion?: number; _auditFingerprint?: string })
            | undefined;
          const storeState = typeof useStore.getState === "function" ? useStore.getState() : undefined;
          const updatedSite = storeState?.siteRag?.sites?.find((s) => s.id === siteId);
          if (typeof receiptMeta?._auditVersion === 'number') {
            runningAuditVersion = receiptMeta._auditVersion;
          } else if (updatedSite && typeof updatedSite.auditOverridesVersion === 'number' && (runningAuditVersion === undefined || updatedSite.auditOverridesVersion > runningAuditVersion)) {
            runningAuditVersion = updatedSite.auditOverridesVersion;
          } else {
            runningAuditVersion = (runningAuditVersion ?? 0) + 1;
          }
          if (typeof receiptMeta?._auditFingerprint === 'string') {
            runningAuditFingerprint = receiptMeta._auditFingerprint;
          } else if (updatedSite?.auditOverrides) {
            runningAuditFingerprint = computeAuditOverridesFingerprint(updatedSite.auditOverrides);
          } else {
            runningAuditFingerprint = undefined;
          }
          if (updatedSite?.auditOverrides) {
            setRefreshedOverrides(updatedSite.auditOverrides);
          }
          setRefreshedAuditVersion(runningAuditVersion);

          if (requiresReportCorrectionApply) {
            reportCorrectionsToApply.push({
              questionId: question.id,
              detector: question.detector,
              originalClaim: question.reportClaim,
              replacementText: a.replacementText.trim(),
              evidenceCitation: a.sourceCitation.trim() || '[Ref 1]',
              assumptionLabel: a.assumptionLabel.trim() || undefined,
              rationale: a.rationale.trim(),
              sourceType: a.sourceType,
              evidenceDocumentId: a.evidenceDocumentId.trim() || undefined,
              evidenceAttachment,
            });
            pendingCorrectionFinalizations.push({
              question,
              finalDraftAttestation: buildTruthAttestation({
                detector: question.detector,
                message: question.reportClaim,
                checkId: question.id,
                rationale: a.rationale.trim(),
                ...(finalResolutionKind !== 'attested' ? { resolution: finalResolutionKind } : {}),
                ...(a.assumptionLabel.trim() ? { assumptionLabel: a.assumptionLabel.trim() } : {}),
                ...(hasExtraMetadata ? { sourceType: a.sourceType } : {}),
                ...(a.sourceCitation.trim() ? { sourceCitation: a.sourceCitation.trim() } : {}),
                ...(a.evidenceDocumentId.trim() ? { evidenceDocumentId: a.evidenceDocumentId.trim() } : {}),
                ...(evidenceAttachment ? { evidenceAttachment } : {}),
                ...(a.choice === ATTESTATION_CORRECT_CLAIM && a.replacementText.trim()
                  ? { replacementText: a.replacementText.trim() }
                  : {}),
                ...(question.reportClaim ? { priorEvidence: question.reportClaim } : {}),
                ...(reportTypeId ? { reportTypeId } : {}),
                ...(savedOverride && typeof savedOverride.id === 'string' && savedOverride.id
                  ? { supersedesOverrideId: savedOverride.id }
                  : {}),
              }),
            });
          } else {
            saved += 1;
            if (unknownQuestion) unknownSaved += 1;
            else resolvedSaved += 1;
          }
        } catch (e) {
          const errObj = e as { message?: string; code?: string; status?: number };
          const msg = errObj?.message ?? 'could not record';
          if (
            errObj?.code === 'AUDIT_OVERRIDE_CONFLICT' ||
            errObj?.code === 'AUDIT_OPERATION_ID_MISMATCH' ||
            errObj?.status === 409 ||
            /conflict|changed since loaded|concurrently|operationid/i.test(msg)
          ) {
            setConflictNotice(msg);
          }
          failures.push(`${question.detector}: ${msg}`);
        }
        continue;
      }

      const settled = settledValue(question);
      if (settled === undefined || 'error' in settled) continue;
      const value = settled.value;
      const path = resolveWritablePath(question);
      if (!path) continue;
      const answer = answers[question.id]!;

      if (!effectiveSed?.id) {
        failures.push(
          `${question.factId ?? path}: site evidence could not be loaded, so this value correction was not saved`,
        );
        continue;
      }

      const reasonParts = [`Truth resolution — ${question.question}`];
      if (answer.assumptionLabel.trim()) {
        reasonParts.push(`Assumption: ${answer.assumptionLabel.trim()}`);
      }
      if (answer.rationale.trim()) {
        reasonParts.push(`Rationale: ${answer.rationale.trim()}`);
      }
      if (answer.sourceCitation.trim()) {
        reasonParts.push(`Source: ${answer.sourceCitation.trim()}`);
      }
      if (answer.evidenceDocumentId.trim() || answer.evidenceNote.trim() || answer.attachedFileName.trim()) {
        const ev = [answer.evidenceDocumentId.trim(), answer.attachedFileName.trim(), answer.evidenceNote.trim()]
          .filter(Boolean)
          .join(' / ');
        reasonParts.push(`Evidence: ${ev}`);
      }

      const currentSedVer = runningSedVersion ?? effectiveSed.version;
      const sourceObj: AmendmentSource = {
        type: answer.sourceType,
        label: SOURCE_OPTIONS.find(o => o.value === answer.sourceType)?.label ?? 'Operator correction',
        ...(answer.sourceCitation.trim() ? { url: answer.sourceCitation.trim() } : {}),
        ...(answer.evidenceDocumentId.trim() ? { documentId: answer.evidenceDocumentId.trim() } : {}),
        ...(answer.evidenceNote.trim() || answer.rationale.trim()
          ? { excerpt: answer.evidenceNote.trim() || answer.rationale.trim() }
          : {}),
      };
      const domain = path.split('.')[0] ?? 'site';
      const sedPayloadSig = computeSedAmendmentPayloadSignature({
        sedId: effectiveSed.id,
        variablePath: path,
        domain,
        oldValue: question.reportClaim,
        newValue: value,
        reason: reasonParts.join(' | '),
        source: sourceObj,
      });
      const sedEnvelope = resolveQuestionCommandEnvelope(
        `sed:${effectiveSed.id}:${question.id}`,
        `truth-sed:${effectiveSed.id}:${question.id}`,
        sedPayloadSig,
        {
          expectedSedVersion: currentSedVer,
        },
      );
      const amendment: SEDAmendment = {
        id: `amend-truth-${question.id}-${Date.now()}`,
        sedId: effectiveSed.id,
        sedVersion: sedEnvelope.expectedSedVersion ?? currentSedVer,
        variablePath: path,
        variableLabel: question.factId ?? path,
        domain: path.split('.')[0] ?? 'site',
        oldValue: question.reportClaim,
        newValue: value,
        reason: reasonParts.join(' | '),
        source: sourceObj,
        createdBy: user.uid,
        createdByEmail: user.email || '',
        createdAt: new Date().toISOString(),
        status: 'pending',
        operationId: sedEnvelope.operationId,
      };

      const result = await saveSEDAmendment(amendment, {
        expectedSedVersion: sedEnvelope.expectedSedVersion ?? currentSedVer,
        operationId: sedEnvelope.operationId,
      });
      if (result.ok) {
        saved += 1;
        if (result.applied === false) {
          pendingReviewSaved += 1;
        } else {
          const nextSedVer =
            typeof result.sedVersion === 'number'
              ? result.sedVersion
              : typeof currentSedVer === 'number'
                ? currentSedVer + 1
                : effectiveSed.version;
          runningSedVersion = nextSedVer;
          effectiveSed = { id: effectiveSed.id, version: nextSedVer };
          setRefreshedSed({ id: effectiveSed.id, version: nextSedVer });
          resolvedSaved += 1;
        }
      } else {
        if (
          result.conflict ||
          result.code === 'SED_VERSION_CONFLICT' ||
          result.code === 'SED_OPERATION_ID_MISMATCH' ||
          /version conflict|changed since|operationid/i.test(result.error)
        ) {
          setConflictNotice(result.error);
        }
        failures.push(`${question.factId ?? path}: ${result.error}`);
      }
    }

    if (reportCorrectionsToApply.length > 0 && failures.length === 0) {
      if (!onApplyReportCorrection) {
        failures.push('Could not apply statement correction to report: correction workflow is unavailable.');
      } else {
        try {
          const corrSig = JSON.stringify(reportCorrectionsToApply);
          const existingRcSlot = operationSlotsRef.current.get(`report-correction:${siteId ?? 'site'}`);
          if (
            (!existingRcSlot || existingRcSlot.payloadSignature !== corrSig) &&
            runningAuditVersion === undefined &&
            !runningAuditFingerprint
          ) {
            failures.push(
              'Could not apply statement correction to report: Authoritative site audit baseline is not loaded; click Refresh latest before saving.',
            );
          } else {
            const reportCorrectionEnvelope = resolveQuestionCommandEnvelope(
              `report-correction:${siteId ?? 'site'}`,
              `truth-report-correction:${siteId ?? 'site'}`,
              corrSig,
              {
                expectedRevisionId: reportRevisionId,
                expectedAuditVersion: runningAuditVersion,
                expectedAuditFingerprint: runningAuditFingerprint,
                expectedSedVersion: runningSedVersion,
              },
            );
            const applyRes = await onApplyReportCorrection(reportCorrectionsToApply, {
              operationId: reportCorrectionEnvelope.operationId,
              ...(reportCorrectionEnvelope.expectedRevisionId
                ? { expectedRevisionId: reportCorrectionEnvelope.expectedRevisionId }
                : {}),
              ...(reportCorrectionEnvelope.expectedAuditVersion !== undefined
                ? { expectedAuditVersion: reportCorrectionEnvelope.expectedAuditVersion }
                : {}),
              ...(reportCorrectionEnvelope.expectedAuditFingerprint
                ? { expectedAuditFingerprint: reportCorrectionEnvelope.expectedAuditFingerprint }
                : {}),
              ...(reportCorrectionEnvelope.expectedSedVersion !== undefined
                ? { expectedSedVersion: reportCorrectionEnvelope.expectedSedVersion }
                : {}),
            });
          if (!applyRes.applied) {
            const msg = applyRes.error ?? applyRes.reason ?? 'Could not apply statement correction to report.';
            if (
              applyRes.conflict ||
              applyRes.code === 'REVISION_CONFLICT' ||
              applyRes.code === 'SED_OR_SITE_CONFLICT' ||
              applyRes.code === 'AUDIT_OVERRIDE_CONFLICT' ||
              /conflict|changed since|reload/i.test(msg)
            ) {
              setConflictNotice(msg);
            }
            failures.push(msg);
          } else if (siteId && pendingCorrectionFinalizations.length > 0) {
            for (const item of pendingCorrectionFinalizations) {
              const finalPayloadSig = computeAuditOverridePayloadSignature(item.finalDraftAttestation);
              const finalEnvelope = resolveQuestionCommandEnvelope(
                `override-finalize:${siteId}:${item.question.id}`,
                `truth-override-finalize:${siteId}:${item.question.id}`,
                finalPayloadSig,
                {
                  expectedAuditVersion: runningAuditVersion,
                  expectedAuditFingerprint: runningAuditFingerprint,
                },
              );
              const finalizedOverride = await recordAuditOverride(
                siteId,
                {
                  ...item.finalDraftAttestation,
                  operationId: finalEnvelope.operationId,
                },
                {
                  expectedAuditVersion: finalEnvelope.expectedAuditVersion,
                  expectedAuditFingerprint: finalEnvelope.expectedAuditFingerprint,
                  operationId: finalEnvelope.operationId,
                },
              );
              const finalReceiptMeta = finalizedOverride as
                | (SiteAuditOverride & { _auditVersion?: number; _auditFingerprint?: string })
                | undefined;
              const storeState = typeof useStore.getState === "function" ? useStore.getState() : undefined;
              const updatedSite = storeState?.siteRag?.sites?.find((s) => s.id === siteId);
              if (typeof finalReceiptMeta?._auditVersion === 'number') {
                runningAuditVersion = finalReceiptMeta._auditVersion;
              } else if (updatedSite && typeof updatedSite.auditOverridesVersion === 'number' && (runningAuditVersion === undefined || updatedSite.auditOverridesVersion > runningAuditVersion)) {
                runningAuditVersion = updatedSite.auditOverridesVersion;
              } else {
                runningAuditVersion = (runningAuditVersion ?? 0) + 1;
              }
              if (typeof finalReceiptMeta?._auditFingerprint === 'string') {
                runningAuditFingerprint = finalReceiptMeta._auditFingerprint;
              } else if (updatedSite?.auditOverrides) {
                runningAuditFingerprint = computeAuditOverridesFingerprint(updatedSite.auditOverrides);
              } else {
                runningAuditFingerprint = undefined;
              }
              if (updatedSite?.auditOverrides) {
                setRefreshedOverrides(updatedSite.auditOverrides);
              }
              setRefreshedAuditVersion(runningAuditVersion);
              saved += 1;
              resolvedSaved += 1;
            }
          }
          }
        } catch (e) {
          const errObj = e as { message?: string; code?: string; status?: number };
          const msg = errObj?.message ?? 'Could not apply statement correction to report.';
          if (
            errObj?.code === 'AUDIT_OVERRIDE_CONFLICT' ||
            errObj?.code === 'AUDIT_OPERATION_ID_MISMATCH' ||
            errObj?.status === 409 ||
            /conflict|changed since|concurrently|operationid|reload/i.test(msg)
          ) {
            setConflictNotice(msg);
          }
          failures.push(msg);
        }
      }
    }

    setIsSaving(false);
    if (saved > 0 && failures.length === 0) {
      if (resolvedSaved > 0) {
        toast.success(
          resolvedSaved === 1
            ? 'Correction saved to site evidence.'
            : `${resolvedSaved} corrections saved to site evidence.`,
        );
        if (announceResolved) onResolved?.(resolvedSaved);
      }
      if (pendingReviewSaved > 0) {
        toast.success(
          pendingReviewSaved === 1
            ? 'Amendment submitted for review (pending approval before applying to site evidence).'
            : `${pendingReviewSaved} amendments submitted for review (pending approval before applying to site evidence).`,
        );
      } else if (resolvedSaved === 0 && unknownSaved > 0) {
        toast.success(
          'Unknown status and rationale recorded (finding remains open).',
        );
      }
    }
    for (const failure of failures) toast.error(`Could not save — ${failure}`);
    if (saved === 0 && failures.length === 0) {
      toast.error('Nothing was saved — answer at least one question first.');
    }
    const clean = failures.length === 0 && saved > 0;
    if (clean) onOpenChange(false);
    return clean;
  };

  const handleRerun = async (): Promise<void> => {
    if (saveableCount > 0 && !(await handleSubmit({ announceResolved: false }))) return;
    onOpenChange(false);
    onRerun?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="truth-resolution-dialog">
        <DialogHeader>
          <DialogTitle>Confirm what's true</DialogTitle>
          <DialogDescription>
            These statements disagree with the site evidence. Settle each one by
            confirming facts, attaching evidence, editing sources, or recording a
            labelled feasibility assumption and rationale.
            {unanswerable > 0 && answerable.length > 0 && (
              <>
                {' '}{answerable.length} of {questions.length} can be
                settled here; the other{unanswerable === 1 ? '' : 's'} {unanswerable === 1 ? 'is' : 'are'} {remainderReason}.
              </>
            )}
            {unanswerable === 0 && advisoryOnly > 0 && Boolean(siteId) && (
              <>
                {' '}All {answerable.length} of {questions.length} can be settled here ({advisoryOnly} not tied to a stored evidence field — settle via narrative correction, explicit unknown, or evidence attestation without overwriting SED variables).
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {conflictNotice && (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-500/60 bg-amber-500/10 px-3 py-2 type-meta text-text-primary"
            data-testid="truth-resolution-conflict-banner"
          >
            <div>
              <span className="font-semibold">Concurrent update detected: </span>
              <span>{conflictNotice} Your draft inputs are preserved.</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handleRefreshConflict()}
              data-testid="truth-resolution-refresh-conflict"
            >
              Refresh latest state
            </Button>
          </div>
        )}

        <div className="max-h-[65vh] space-y-4 overflow-y-auto py-1">
          {answerable.map(question => {
            const answer = getAnswer(question.id);
            const persistedUnknown = retainedUnknownMap.get(question.id);
            const needsRationale = isOverrideChoice(answer?.choice);
            const classification = classifyTruthQuestion(question, resolveWritablePath(question) !== undefined);
            return (
              <div
                key={question.id}
                className="rounded-md border border-border bg-surface-2 p-3"
                data-testid={`truth-question-${question.id}`}
              >
                <div
                  className="mb-1.5 flex flex-wrap items-center justify-between gap-2 rounded-sm bg-background/70 px-2 py-1 type-meta"
                  data-testid={`truth-question-classification-${question.id}`}
                >
                  <span className="font-semibold text-text-primary">{classification.badgeLabel}</span>
                  <span className="text-text-secondary">{classification.actionGuidance}</span>
                </div>
                <div className="type-meta text-text-secondary">The report says</div>
                <div className="mb-2 type-body text-text-primary">{question.reportClaim}</div>
                <div className="mb-2 type-body font-semibold text-text-primary">
                  {question.question}
                </div>
                {persistedUnknown && (
                  <div
                    className="mb-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 type-meta text-text-primary"
                    data-testid={`truth-retained-unknown-status-${question.id}`}
                  >
                    <span className="font-semibold">
                      Status: Retained as Unknown in site evidence (unresolved finding preserved).
                    </span>{' '}
                    Recorded rationale: {persistedUnknown.rationale}
                  </div>
                )}

                {isContextualUnboundQuestion(question) && question.candidates.length > 0 && (
                  <div
                    className="mb-2 rounded-sm border border-border/60 bg-background/60 px-2.5 py-1.5 type-meta text-text-secondary"
                    data-testid={`truth-candidate-values-${question.id}`}
                  >
                    <span className="font-medium text-text-primary">Referenced values: </span>
                    {question.candidates.map(c => formatCandidateDisplayLabel(c)).join(' · ')}
                  </div>
                )}

                <div className="space-y-1.5">
                  {!isContextualUnboundQuestion(question) && question.candidates.map(candidate => (
                    <label key={candidate.value} className="flex items-center gap-2 type-body">
                      <input
                        type="radio"
                        name={question.id}
                        value={candidate.value}
                        checked={answer?.choice === candidate.value}
                        onChange={() => update(question.id, { choice: candidate.value })}
                      />
                      <span>{formatCandidateDisplayLabel(candidate)}</span>
                    </label>
                  ))}
                  {!isContextualUnboundQuestion(question) && question.allowFreeText && (
                    <label className="flex items-center gap-2 type-body">
                      <input
                        type="radio"
                        name={question.id}
                        value={FREE_TEXT}
                        checked={answer?.choice === FREE_TEXT}
                        onChange={() => update(question.id, { choice: FREE_TEXT })}
                      />
                      <input
                        type="text"
                        aria-label={`Correct value for ${question.factId ?? question.id}`}
                        placeholder="Enter the correct value"
                        className="flex-1 rounded-sm border border-border bg-background px-2 py-1 type-body"
                        value={answer?.freeText ?? ''}
                        onChange={e =>
                          update(question.id, { choice: FREE_TEXT, freeText: e.target.value })
                        }
                      />
                    </label>
                  )}
                  {!isContextualUnboundQuestion(question) && question.allowFreeText && unitOf(question) && (() => {
                    const echo = echoFor(question);
                    const failed = settledValue(question);
                    const isError = failed !== undefined && 'error' in failed;
                    return (
                      <div
                        className={`type-meta ${isError ? 'text-destructive' : 'text-text-secondary'}`}
                        data-testid={`truth-unit-echo-${question.id}`}
                      >
                        {echo ?? `Enter a value in ${unitOf(question)} — or use ${acceptedUnits(unitOf(question)!).join(', ')}.`}
                      </div>
                    );
                  })()}
                  {isContextualUnboundQuestion(question) && Boolean(siteId) && (
                    <label className="flex items-center gap-2 type-body">
                      <input
                        type="radio"
                        name={question.id}
                        value={ATTESTATION_CONFIRMED}
                        checked={answer?.choice === ATTESTATION_CONFIRMED}
                        onChange={() => update(question.id, { choice: ATTESTATION_CONFIRMED })}
                        data-testid={`truth-choice-confirmed-${question.id}`}
                      />
                      <span>Yes — contextual / subset figure is accurate in context (do not overwrite site evidence)</span>
                    </label>
                  )}
                  {Boolean(siteId) && canRetainUnknown(question) && (
                    <label className="flex items-center gap-2 type-body">
                      <input
                        type="radio"
                        name={question.id}
                        value={ATTESTATION_UNKNOWN}
                        checked={answer?.choice === ATTESTATION_UNKNOWN}
                        onChange={() =>
                          update(question.id, {
                            choice: ATTESTATION_UNKNOWN,
                            assumptionLabel:
                              answer?.assumptionLabel?.trim()
                              || 'Unknown / unverified at screening stage (retain absent)',
                          })
                        }
                        data-testid={`truth-choice-unknown-${question.id}`}
                      />
                      <span>Keep as Unknown / retain-absent in site evidence (do not set a verified value)</span>
                    </label>
                  )}
                  {Boolean(siteId) && (
                    <label className="flex items-center gap-2 type-body">
                      <input
                        type="radio"
                        name={question.id}
                        value={ATTESTATION_ASSUMPTION}
                        checked={answer?.choice === ATTESTATION_ASSUMPTION}
                        onChange={() =>
                          update(question.id, {
                            choice: ATTESTATION_ASSUMPTION,
                            assumptionLabel:
                              answer?.assumptionLabel?.trim()
                              || 'Feasibility-stage planning assumption',
                          })
                        }
                        data-testid={`truth-choice-assumption-${question.id}`}
                      />
                      <span>Record as labelled feasibility assumption / planning context</span>
                    </label>
                  )}
                  {Boolean(siteId) && (
                    <label className="flex items-center gap-2 type-body">
                      <input
                        type="radio"
                        name={question.id}
                        value={ATTESTATION_CORRECT_CLAIM}
                        checked={answer?.choice === ATTESTATION_CORRECT_CLAIM}
                        onChange={() =>
                          update(question.id, {
                            choice: ATTESTATION_CORRECT_CLAIM,
                            replacementText: answer?.replacementText || question.reportClaim,
                          })
                        }
                        data-testid={`truth-choice-correct-${question.id}`}
                      />
                      <span>Correct statement / fact wording in report</span>
                    </label>
                  )}
                </div>

                {answer?.choice === ATTESTATION_CORRECT_CLAIM && (
                  <div className="mt-2">
                    <label
                      className="type-meta text-text-secondary"
                      htmlFor={`replacement-${question.id}`}
                    >
                      Corrected statement wording
                    </label>
                    <textarea
                      id={`replacement-${question.id}`}
                      rows={2}
                      className="mt-1 w-full rounded-sm border border-border bg-background px-2 py-1 type-body"
                      placeholder="Enter the corrected statement wording supported by site evidence or labelled as a planning assumption."
                      value={answer?.replacementText ?? ''}
                      onChange={e => update(question.id, { replacementText: e.target.value })}
                      data-testid={`truth-replacement-text-${question.id}`}
                    />
                  </div>
                )}

                {question.kind === 'attestation' && answer?.choice === ATTESTATION_RETRACT && (
                  <div className="mt-2 type-meta text-text-secondary" data-testid={`truth-retract-${question.id}`}>
                    The finding stands. {retractRemedy(question)}
                  </div>
                )}

                {/* Always-visible Source Editing, Evidence Attachment, Explicit Assumption Label, and Rationale */}
                <div className="mt-3 space-y-2 border-t border-border/60 pt-2">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      <label
                        className="type-meta text-text-secondary"
                        htmlFor={`source-type-${question.id}`}
                      >
                        How do you know? (Source type)
                      </label>
                      <select
                        id={`source-type-${question.id}`}
                        aria-label={`Source for ${question.factId ?? question.id}`}
                        data-testid={`truth-source-type-${question.id}`}
                        className="rounded-sm border border-border bg-background px-2 py-1 type-meta"
                        value={answer?.sourceType ?? 'expert-judgment'}
                        onChange={e =>
                          update(question.id, { sourceType: e.target.value as AmendmentSource['type'] })
                        }
                      >
                        {SOURCE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label
                        className="type-meta text-text-secondary"
                        htmlFor={`source-citation-${question.id}`}
                      >
                        Source citation / reference
                      </label>
                      <input
                        id={`source-citation-${question.id}`}
                        type="text"
                        aria-label={`Source citation for ${question.factId ?? question.id}`}
                        data-testid={`truth-source-citation-${question.id}`}
                        placeholder="e.g. [Ref 1], utility study, or sed.grid.capacityMW"
                        className="rounded-sm border border-border bg-background px-2 py-1 type-meta"
                        value={answer?.sourceCitation ?? ''}
                        onChange={e => update(question.id, { sourceCitation: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      <label
                        className="type-meta text-text-secondary"
                        htmlFor={`assumption-preset-${question.id}`}
                      >
                        Explicit assumption label
                      </label>
                      <select
                        id={`assumption-preset-${question.id}`}
                        aria-label={`Assumption preset for ${question.id}`}
                        data-testid={`truth-assumption-preset-${question.id}`}
                        className="rounded-sm border border-border bg-background px-2 py-1 type-meta"
                        value={
                          ASSUMPTION_PRESETS.some(p => p.value === (answer?.assumptionLabel ?? ''))
                            ? (answer?.assumptionLabel ?? '')
                            : '__custom__'
                        }
                        onChange={e => {
                          const val = e.target.value;
                          if (val !== '__custom__') {
                            update(question.id, { assumptionLabel: val });
                          }
                        }}
                      >
                        {ASSUMPTION_PRESETS.map(p => (
                          <option key={p.label} value={p.value}>{p.label}</option>
                        ))}
                        <option value="__custom__">Custom assumption label…</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label
                        className="type-meta text-text-secondary"
                        htmlFor={`assumption-label-${question.id}`}
                      >
                        Assumption label text
                      </label>
                      <input
                        id={`assumption-label-${question.id}`}
                        type="text"
                        aria-label={`Assumption label for ${question.id}`}
                        data-testid={`truth-assumption-label-${question.id}`}
                        placeholder="e.g. Feasibility-stage planning assumption"
                        className="rounded-sm border border-border bg-background px-2 py-1 type-meta"
                        value={answer?.assumptionLabel ?? ''}
                        onChange={e => update(question.id, { assumptionLabel: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      <label
                        className="type-meta text-text-secondary"
                        htmlFor={`evidence-doc-${question.id}`}
                      >
                        Attach site document evidence
                      </label>
                      <select
                        id={`evidence-doc-${question.id}`}
                        aria-label={`Attach evidence document for ${question.id}`}
                        data-testid={`truth-evidence-doc-${question.id}`}
                        className="rounded-sm border border-border bg-background px-2 py-1 type-meta"
                        value={answer?.evidenceDocumentId ?? ''}
                        onChange={e => update(question.id, { evidenceDocumentId: e.target.value })}
                      >
                        <option value="">No linked site document</option>
                        {availableDocuments.map(doc => (
                          <option key={doc.id} value={doc.id}>
                            {doc.name}{doc.category ? ` (${doc.category})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label
                        className="type-meta text-text-secondary"
                        htmlFor={`evidence-note-${question.id}`}
                      >
                        Evidence excerpt / URL / file attachment
                      </label>
                      <div className="flex items-center gap-1.5">
                        <input
                          id={`evidence-note-${question.id}`}
                          type="text"
                          aria-label={`Evidence note or URL for ${question.id}`}
                          data-testid={`truth-evidence-note-${question.id}`}
                          placeholder="Excerpt, section, or URL…"
                          className="flex-1 rounded-sm border border-border bg-background px-2 py-1 type-meta"
                          value={answer?.evidenceNote ?? ''}
                          onChange={e => update(question.id, { evidenceNote: e.target.value })}
                        />
                        <input
                          type="file"
                          aria-label={`Attach evidence file for ${question.id}`}
                          data-testid={`truth-evidence-file-${question.id}`}
                          className="max-w-[140px] text-[11px] text-text-secondary"
                          onChange={e => {
                            const fileName = e.target.files?.[0]?.name ?? '';
                            update(question.id, { attachedFileName: fileName });
                          }}
                        />
                      </div>
                      {answer?.attachedFileName && (
                        <span
                          className="type-meta text-text-secondary"
                          data-testid={`truth-attached-filename-${question.id}`}
                        >
                          Attached: {answer.attachedFileName}
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="type-meta text-text-secondary" htmlFor={`rationale-${question.id}`}>
                      Why is it accurate or stage-appropriate? (Rationale recorded on the audit trail)
                    </label>
                    <textarea
                      id={`rationale-${question.id}`}
                      rows={2}
                      className="mt-1 w-full rounded-sm border border-border bg-background px-2 py-1 type-body"
                      placeholder="e.g. Confirmed with the utility on 5 Aug; or Feasibility-stage planning assumption subject to Phase 2 interconnection study."
                      value={answer?.rationale ?? ''}
                      onChange={e => update(question.id, { rationale: e.target.value })}
                      data-testid={`truth-rationale-${question.id}`}
                    />
                    {needsRationale && (answer?.rationale ?? '').trim().length < MIN_RATIONALE && (
                      <div className="type-meta text-text-secondary">
                        This overrules the evidence, so the reason is required.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {answerable.length === 0 && (
            <div className="type-body text-text-secondary" data-testid="truth-no-answerable">
              These findings are {remainderReason}, so they can't be settled here.
              Review them in the report-quality findings, then rerun the report.
            </div>
          )}
          {unanswerable > 0 && answerable.length > 0 && (
            <div className="type-meta text-text-secondary">
              {unanswerable} further finding{unanswerable === 1 ? " can't" : "s can't"} be settled
              here — review {unanswerable === 1 ? 'it' : 'them'} in the report-quality findings.
            </div>
          )}
          {(unanswerable > 0 || answerable.length === 0) && onOpenQuality && (
            <button
              type="button"
              onClick={() => { onOpenChange(false); onOpenQuality(); }}
              data-testid="truth-open-quality"
              className="type-meta text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Open quality findings
            </button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          {retractCount > 0 && onRerun && (
            <Button
              variant={settledCount === 0 ? 'default' : 'outline'}
              onClick={() => { void handleRerun(); }}
              disabled={isSaving}
              data-testid="truth-resolution-rerun"
            >
              Rerun report
            </Button>
          )}
          <Button
            onClick={() => { void handleSubmit(); }}
            disabled={isSaving || saveableCount === 0}
            data-testid="truth-resolution-save"
          >
            {isSaving
              ? 'Saving…'
              : settledCount === 0 && retainedUnknownCount > 0
                ? `Record ${retainedUnknownCount} unknown status`
                : `Save ${saveableCount || ''} correction${saveableCount === 1 ? '' : 's'}`.replace('  ', ' ')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
